import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import SidePanel from '../util/SidePanel';
import CircularProgress from '@mui/material/CircularProgress';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import EditTemplate from '../modal/EditTemplate';

const DAY: Record<number, string> = { 1:'Mon', 2:'Tue', 3:'Wed', 4:'Thu', 5:'Fri', 6:'Sat', 7:'Sun' };

const SvgEdit  = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const SvgTrash = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>;
const SvgX     = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const SvgPlus  = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const SvgChev  = ({ open }: { open: boolean }) => (
  <svg style={{ transition: 'transform .2s', transform: open ? 'rotate(90deg)' : 'none', flexShrink: 0 }}
       width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const abtn = (variant?: 'del'): React.CSSProperties => ({
  width: 26, height: 26, borderRadius: 7,
  border: '1px solid var(--border-input)',
  background: 'var(--btn-user-form)',
  color: variant === 'del' ? '#dc2626' : 'var(--text-color)',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'all .15s', flexShrink: 0,
});

interface TplEntry {
  id: number;
  temp_group_number: string;
  temp_name: string;
  day_number: number;
  startTime: string;
  endTime: string;
  categoryId: number;
  taskId: string;
  description: string;
}

export interface PendingEntry {
  selectedDay: number;
  startTime: string;
  endTime: string;
  categoryId: number;
  tasksId: { value: number; label: string }[];
  description: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  allTemplate: TplEntry[];
  allCategory: any[];
  refresh: () => void;
  refreshSelectTemplate: () => void;
  usersSuperId: string;
  is24HourFormat: boolean;
  taskOrTasks: any;
  openCheckModal: boolean;
  pendingEntries: PendingEntry[];
  onApplyTemplate: (groupNumber: string, entryIds?: number[]) => void;
}

const TemplatesPanel: React.FC<Props> = ({
  open, onClose, allTemplate, allCategory, refresh, refreshSelectTemplate,
  usersSuperId, is24HourFormat, taskOrTasks, openCheckModal,
  pendingEntries, onApplyTemplate,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [expandedGroup, setExpandedGroup]     = useState<string | null>(null);
  const [deletingGroup, setDeletingGroup]     = useState<string | null>(null);
  const [deletingEntry, setDeletingEntry]     = useState<number | null>(null);
  const [editingEntryId, setEditingEntryId]   = useState<number | null>(null); // kept for drawer fallback
  const [hoveredEntry, setHoveredEntry]       = useState<number | null>(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<number>>(new Set());

  // Inline edit state for existing entries
  const [inlineEditId, setInlineEditId]       = useState<number | null>(null);
  const [inlineDay, setInlineDay]             = useState(1);
  const [inlineStart, setInlineStart]         = useState('09:00');
  const [inlineEnd, setInlineEnd]             = useState('17:00');
  const [inlineCatId, setInlineCatId]         = useState<number | ''>('');
  const [inlineTaskOpts, setInlineTaskOpts]   = useState<{value:number;label:string}[]>([]);
  const [inlineTaskId, setInlineTaskId]       = useState<number | ''>('');
  const [savingInline, setSavingInline]       = useState(false);

  useEffect(() => {
    if (!inlineCatId) { setInlineTaskOpts([]); setInlineTaskId(''); return; }
    axios.get(`category/${inlineCatId}`)
      .then(({ data }) => setInlineTaskOpts((data.task||[]).filter((t:any)=>t.active).map((t:any)=>({value:t.id,label:t.name}))))
      .catch(()=>setInlineTaskOpts([]));
  }, [inlineCatId]);

  const openInlineEdit = (entry: TplEntry) => {
    setInlineEditId(entry.id);
    setInlineDay(Number(entry.day_number));
    setInlineStart(entry.startTime);
    setInlineEnd(entry.endTime);
    setInlineCatId(entry.categoryId || '');
    setInlineTaskId(entry.taskId ? Number(entry.taskId.split(',')[0]) : '');
    setDeletingEntry(null);
  };

  const saveInlineEdit = async () => {
    if (!inlineEditId) return;
    setSavingInline(true);
    try {
      await axios.put(`template/${inlineEditId}`, {
        day_number: inlineDay,
        endDay_number: inlineDay,
        startTime: inlineStart,
        endTime: inlineEnd,
        categoryId: Number(inlineCatId),
        taskId: inlineTaskId ? String(inlineTaskId) : '',
      });
      refresh();
      setInlineEditId(null);
      enqueueSnackbar('Entry updated', { variant: 'success' });
    } catch { enqueueSnackbar('Error updating entry', { variant: 'error' }); }
    finally { setSavingInline(false); }
  };
  const [showNewForm, setShowNewForm]         = useState(false);
  const [newName, setNewName]                 = useState('');
  const [newEntries, setNewEntries]           = useState<PendingEntry[]>([]);
  const [saving, setSaving]                   = useState(false);
  const [tasks, setTasks]                     = useState<any[]>([]);
  const nameRef = useRef<HTMLInputElement>(null);

  // Inline add-row form state
  const [showRowForm, setShowRowForm]         = useState(false);
  const [rowDay, setRowDay]                   = useState(1);
  const [rowStart, setRowStart]               = useState('09:00');
  const [rowEnd, setRowEnd]                   = useState('17:00');
  const [rowCatId, setRowCatId]               = useState<number | ''>('');
  const [rowTaskOptions, setRowTaskOptions]   = useState<{value:number;label:string}[]>([]);
  const [rowTaskIds, setRowTaskIds]           = useState<number[]>([]);

  // Fetch tasks when category changes in row form
  useEffect(() => {
    if (!rowCatId) { setRowTaskOptions([]); setRowTaskIds([]); return; }
    axios.get(`category/${rowCatId}`)
      .then(({ data }) => {
        const opts = (data.task || []).filter((t: any) => t.active).map((t: any) => ({ value: t.id, label: t.name }));
        setRowTaskOptions(opts);
        setRowTaskIds([]);
      })
      .catch(() => { setRowTaskOptions([]); setRowTaskIds([]); });
  }, [rowCatId]);

  const addRowToEntries = () => {
    if (!rowCatId) { enqueueSnackbar('Select a category', { variant: 'warning' }); return; }
    const catNode = allCategory.find(c => c.id === rowCatId);
    const tasksId = rowTaskIds.map(id => ({ value: id, label: rowTaskOptions.find(o => o.value === id)?.label || '' }));
    setNewEntries(prev => [...prev, {
      selectedDay: rowDay,
      startTime: rowStart,
      endTime: rowEnd,
      categoryId: rowCatId,
      tasksId,
      description: '',
    }]);
    // keep form open for next row, just reset task selection
    setRowTaskIds([]);
    setShowRowForm(false);
  };

  useEffect(() => {
    if (open) {
      refresh(); // load template entries (allTemplate is only the select-list until now)
      axios.get('task').then(({ data }) => setTasks(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When panel opens with pending entries → show new form pre-filled
  useEffect(() => {
    if (open && pendingEntries.length > 0) {
      setNewEntries(pendingEntries);
      setShowNewForm(true);
      setNewName('');
      setTimeout(() => nameRef.current?.focus(), 150);
    }
  }, [open, pendingEntries]);

  const handleClose = () => {
    setShowNewForm(false);
    setNewName('');
    setNewEntries([]);
    setDeletingGroup(null);
    setDeletingEntry(null);
    onClose();
  };

  // Group entries by temp_group_number
  const groups = useMemo(() => {
    const map: Record<string, { name: string; entries: TplEntry[] }> = {};
    allTemplate.forEach(e => {
      const gn = String(e.temp_group_number);
      if (!map[gn]) map[gn] = { name: e.temp_name, entries: [] };
      map[gn].entries.push(e);
    });
    return Object.entries(map).map(([gn, g]) => ({
      groupNumber: gn,
      name: g.name,
      entries: g.entries.sort((a, b) => a.day_number - b.day_number || a.startTime.localeCompare(b.startTime)),
    }));
  }, [allTemplate]);

  const catPath = useCallback((id: number) => {
    const cat = allCategory.find(c => c.id === id);
    if (!cat) return '';
    let path = cat.name;
    let pid = cat.parentId;
    while (pid) {
      const p = allCategory.find(c => c.id === pid);
      if (p) { path = `${p.name} › ${path}`; pid = p.parentId; } else break;
    }
    return path;
  }, [allCategory]);

  const resolveTaskNames = useCallback((taskId: string) => {
    if (!taskId || !tasks.length) return '';
    return taskId.split(',')
      .map(id => tasks.find(tk => tk.id === Number(id.trim()))?.name || '')
      .filter(Boolean).join(', ');
  }, [tasks]);

  const deleteGroup = async (gn: string) => {
    try {
      await axios.delete(`/template/group/${gn}`);
      refresh(); refreshSelectTemplate();
      setDeletingGroup(null);
      if (expandedGroup === gn) setExpandedGroup(null);
      enqueueSnackbar('Template deleted', { variant: 'success' });
    } catch { enqueueSnackbar('Error deleting template', { variant: 'error' }); }
  };

  const deleteEntry = async (id: number) => {
    try {
      await axios.delete(`/template/${id}`);
      refresh();
      setDeletingEntry(null);
      enqueueSnackbar('Entry deleted', { variant: 'success' });
    } catch { enqueueSnackbar('Error deleting entry', { variant: 'error' }); }
  };

  const saveNewTemplate = async () => {
    if (!newName.trim()) { nameRef.current?.focus(); enqueueSnackbar(t('AddName') || 'Enter a name', { variant: 'error' }); return; }
    setSaving(true);
    try {
      await axios.post('template/add/', {
        temp_name: newName.trim(),
        ...(usersSuperId !== 'none' && { userId: Number(usersSuperId) }),
        tmpArray: newEntries.map(e => ({
          day_number: Number(e.selectedDay),
          endDay_number: Number(e.selectedDay),
          startTime: e.startTime,
          endTime: e.endTime,
          description: e.description,
          taskId: e.tasksId ? e.tasksId.map(tk => tk.value).join(',') : '',
          categoryId: Number(e.categoryId),
          comments: '',
        })),
      });
      refresh(); refreshSelectTemplate();
      setShowNewForm(false); setNewName(''); setNewEntries([]);
      enqueueSnackbar(`${t('Add') || 'Saved'} "${newName.trim()}"`, { variant: 'success' });
    } catch { enqueueSnackbar('Error saving template', { variant: 'error' }); }
    finally { setSaving(false); }
  };

  // Shared styles
  const S = {
    panel: { width: 418, background: 'var(--bg-form)', borderLeft: '1px solid var(--border-input)', display: 'flex', flexDirection: 'column' as const, height: '100%', overflow: 'hidden' },
    sectionHead: { fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase' as const, color: 'var(--text-color-calendar-week)' },
  };

  return (
    <>
      <SidePanel open={open} onClose={handleClose} width={418} paperStyle={{ borderLeft: '1px solid var(--border-input)', display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border-input)', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-color)', letterSpacing: '-.01em' }}>{t('Templates') || 'Templates'}</div>
            <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>Click to expand · hover for actions</div>
          </div>
          <button type="button" onClick={handleClose} style={abtn()} title="Close">
            <SvgX />
          </button>
        </div>

        {/* + New template button */}
        <div style={{ padding: '8px 12px 5px', flexShrink: 0 }}>
          <button type="button"
            onClick={() => { setShowNewForm(true); setNewEntries([]); setNewName(''); setTimeout(() => nameRef.current?.focus(), 80); }}
            style={{ width: '100%', height: 30, borderRadius: 8, border: '1.5px dashed var(--border-input)', background: 'transparent', color: 'var(--text-color-calendar-week)', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all .15s' }}
          >
            <SvgPlus /> {t('NewTemplate') || 'New template'}
          </button>
        </div>

        {/* Scrollable list */}
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 16 }}>

          {/* ── New template form ── */}
          {showNewForm && (
            <div style={{ background: 'rgba(124,58,237,.04)', borderBottom: '2px solid rgba(124,58,237,.18)', marginBottom: 4 }}>

              {/* Name */}
              <div style={{ padding: '10px 12px 8px' }}>
                <div style={{ ...S.sectionHead, color: '#7c3aed', marginBottom: 5 }}>Template name</div>
                <input
                  ref={nameRef}
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { setShowNewForm(false); setNewEntries([]); } }}
                  placeholder={`${t('EnterNameOf') || 'Template name'}...`}
                  maxLength={40}
                  style={{ width: '100%', height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: '1.5px solid rgba(124,58,237,.4)', borderRadius: 8, color: 'var(--text-color)', fontSize: 12, fontWeight: 700, padding: '0 10px', outline: 'none' }}
                />
              </div>

              {/* Inline add-row form */}
              <div style={{ borderTop: '1px solid var(--border-input)', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ ...S.sectionHead, color: '#7c3aed' }}>Add entry</div>

                {/* Row 1: Day + From + To */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select
                    value={rowDay}
                    onChange={e => setRowDay(Number(e.target.value))}
                    style={{ flex: 1, height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: 'var(--text-color)', fontSize: 12, padding: '0 6px', cursor: 'pointer' }}>
                    {Object.entries(DAY).map(([num, name]) => <option key={num} value={num}>{name}</option>)}
                  </select>
                  <input type="time" value={rowStart} onChange={e => setRowStart(e.target.value)}
                    style={{ width: 80, height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: 'var(--text-color)', fontSize: 12, padding: '0 6px' }} />
                  <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>→</span>
                  <input type="time" value={rowEnd} onChange={e => setRowEnd(e.target.value)}
                    style={{ width: 80, height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: 'var(--text-color)', fontSize: 12, padding: '0 6px' }} />
                </div>

                {/* Row 2: Category + Tasks */}
                <div style={{ display: 'flex', gap: 6 }}>
                  <select
                    value={rowCatId}
                    onChange={e => setRowCatId(e.target.value ? Number(e.target.value) : '')}
                    style={{ flex: 1, height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: rowCatId ? 'var(--text-color)' : 'var(--text-color-calendar-week)', fontSize: 12, padding: '0 6px', cursor: 'pointer' }}>
                    <option value="">Category...</option>
                    {allCategory
                      .filter(c => !allCategory.some(x => x.parentId === c.id))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(c => <option key={c.id} value={c.id}>{catPath(c.id)}</option>)}
                  </select>
                  {rowTaskOptions.length > 0 && (
                    <select
                      value={rowTaskIds[0] ?? ''}
                      onChange={e => setRowTaskIds(e.target.value ? [Number(e.target.value)] : [])}
                      style={{ flex: 1, height: 30, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 7, color: rowTaskIds.length ? 'var(--text-color)' : 'var(--text-color-calendar-week)', fontSize: 12, padding: '0 6px', cursor: 'pointer' }}>
                      <option value="">Task (optional)</option>
                      {rowTaskOptions.sort((a,b) => a.label.localeCompare(b.label)).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </div>

                {/* Add row button */}
                <button type="button" onClick={addRowToEntries}
                  style={{ height: 30, borderRadius: 8, border: '1.5px dashed rgba(124,58,237,.4)', background: 'transparent', color: '#7c3aed', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all .15s' }}>
                  <SvgPlus /> Add to list
                </button>
              </div>

              {/* Added entries list */}
              {newEntries.length > 0 && (
                <div style={{ borderTop: '1px solid var(--border-input)' }}>
                  <div style={{ ...S.sectionHead, color: '#7c3aed', padding: '8px 14px 4px' }}>{newEntries.length} {newEntries.length === 1 ? 'entry' : 'entries'}</div>
                  {newEntries.map((e, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderTop: '1px solid var(--border-input)' }}>
                      <div style={{ width: 28, fontSize: 10, fontWeight: 800, color: 'var(--text-color-calendar-week)', textTransform: 'uppercase' as const, flexShrink: 0 }}>{DAY[e.selectedDay]}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2, #5a6880)', fontVariantNumeric: 'tabular-nums' as const, flexShrink: 0, whiteSpace: 'nowrap' as const }}>{e.startTime}–{e.endTime}</div>
                      <div style={{ flex: 1, minWidth: 0, marginLeft: 4 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{catPath(e.categoryId)}</div>
                        {e.tasksId?.length > 0 && <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{e.tasksId.map(tk => tk.label).join(', ')}</div>}
                      </div>
                      <button type="button" style={abtn('del')} onClick={() => setNewEntries(prev => prev.filter((_, j) => j !== i))} title="Remove"><SvgX /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderTop: '1px solid var(--border-input)' }}>
                <button type="button" onClick={saveNewTemplate} disabled={saving}
                  style={{ height: 32, padding: '0 18px', borderRadius: 9, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#2563eb)', color: 'white', fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? .7 : 1 }}>
                  {saving && <CircularProgress size={11} color="inherit" />}
                  {t('SaveTemplate') || 'Save template'}
                </button>
                <button type="button" onClick={() => { setShowNewForm(false); setNewName(''); setNewEntries([]); setShowRowForm(false); }}
                  style={{ height: 32, padding: '0 14px', borderRadius: 9, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  {t('Cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {groups.length === 0 && !showNewForm && (
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: 'var(--text-color-calendar-week)' }}>
              {t('Empty') || 'No templates yet'}
            </div>
          )}

          {/* ── Template list ── */}
          {groups.map(group => {
            const isExpanded = expandedGroup === group.groupNumber;
            return (
              <div key={group.groupNumber} style={{ borderBottom: '1px solid var(--border-input)' }}>

                {/* Template header row */}
                <div
                  style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', cursor: 'pointer', transition: 'background .12s', background: isExpanded ? 'rgba(124,58,237,.05)' : 'transparent', userSelect: 'none' as const }}
                  onMouseEnter={e => { if (!isExpanded) (e.currentTarget as HTMLElement).style.background = 'var(--none-button, var(--btn-user-form))'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isExpanded ? 'rgba(124,58,237,.05)' : 'transparent'; }}
                  onClick={e => {
                    if ((e.target as HTMLElement).closest('[data-no-toggle]')) return;
                    setExpandedGroup(isExpanded ? null : group.groupNumber);
                    setSelectedEntryIds(new Set());
                    setDeletingGroup(null);
                    setDeletingEntry(null);
                    setInlineEditId(null);
                  }}
                >
                  <span style={{ width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isExpanded ? '#7c3aed' : 'var(--text-color-calendar-week)', marginRight: 7, flexShrink: 0 }}>
                    <SvgChev open={isExpanded} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: isExpanded ? '#7c3aed' : 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                      {group.name}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1 }}>
                      {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
                    </div>
                  </div>
                  <div data-no-toggle="" style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                    {/* Apply — рядом с названием */}
                    <button type="button" data-no-toggle=""
                      onClick={e => { e.stopPropagation(); onApplyTemplate(group.groupNumber); handleClose(); }}
                      style={{ height: 24, padding: '0 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' as const }}>
                      {t('Apply') || 'Apply'}
                    </button>
                    <button type="button" data-no-toggle="" style={abtn('del')} onClick={e => { e.stopPropagation(); setDeletingGroup(g => g === group.groupNumber ? null : group.groupNumber); setDeletingEntry(null); }} title={t('Delete') || 'Delete'}>
                      <SvgTrash />
                    </button>
                  </div>
                </div>

                {/* Delete group confirm */}
                {deletingGroup === group.groupNumber && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'rgba(239,68,68,.07)', borderTop: '1px solid rgba(239,68,68,.15)' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#dc2626', flex: 1 }}>Delete "{group.name}"?</span>
                    <button type="button" onClick={() => deleteGroup(group.groupNumber)} style={{ padding: '3px 10px', borderRadius: 6, background: '#dc2626', color: 'white', border: 'none', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>{t('Yes')}</button>
                    <button type="button" onClick={() => setDeletingGroup(null)} style={{ padding: '3px 10px', borderRadius: 6, background: 'var(--btn-user-form)', color: 'var(--text-color)', border: '1px solid var(--border-input)', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>{t('Cancel')}</button>
                  </div>
                )}

                {/* ── Expanded entries ── */}
                {isExpanded && (
                  <div style={{ background: 'rgba(124,58,237,.03)', borderTop: '1px solid var(--border-input)' }}>

                    {/* Select all / Apply selected bar */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 12px', borderBottom: '1px solid var(--border-input)', background: 'rgba(124,58,237,.04)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'var(--text-color-calendar-week)', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          style={{ cursor: 'pointer' }}
                          checked={group.entries.length > 0 && group.entries.every(e => selectedEntryIds.has(e.id))}
                          onChange={e => {
                            if (e.target.checked) setSelectedEntryIds(new Set(group.entries.map(e => e.id)));
                            else setSelectedEntryIds(new Set());
                          }}
                        />
                        {selectedEntryIds.size > 0
                          ? `${selectedEntryIds.size} of ${group.entries.length} selected`
                          : 'Select all'}
                      </label>
                      {selectedEntryIds.size > 0 && (
                        <button type="button" data-no-toggle=""
                          onClick={() => { onApplyTemplate(group.groupNumber, Array.from(selectedEntryIds)); handleClose(); }}
                          style={{ height: 22, padding: '0 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          Apply selected ({selectedEntryIds.size})
                        </button>
                      )}
                    </div>

                    {/* Entry rows */}
                    {group.entries.map(entry => (
                      <div key={entry.id} style={{ borderBottom: '1px solid var(--border-input)' }}>

                        {/* Delete confirm */}
                        {deletingEntry === entry.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'rgba(239,68,68,.07)' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#dc2626', flex: 1 }}>Delete this entry?</span>
                            <button type="button" onClick={() => deleteEntry(entry.id)} style={{ padding: '3px 10px', borderRadius: 6, background: '#dc2626', color: 'white', border: 'none', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>{t('Yes')}</button>
                            <button type="button" onClick={() => setDeletingEntry(null)} style={{ padding: '3px 10px', borderRadius: 6, background: 'var(--btn-user-form)', color: 'var(--text-color)', border: '1px solid var(--border-input)', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>{t('Cancel')}</button>
                          </div>

                        ) : inlineEditId === entry.id ? (
                          /* ── Inline edit form ── */
                          <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6, background: 'rgba(124,58,237,.05)' }}>
                            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                              <select value={inlineDay} onChange={e => setInlineDay(Number(e.target.value))}
                                style={{ flex: 1, height: 28, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 6, color: 'var(--text-color)', fontSize: 11, padding: '0 5px' }}>
                                {Object.entries(DAY).map(([n, d]) => <option key={n} value={n}>{d}</option>)}
                              </select>
                              <input type="time" value={inlineStart} onChange={e => setInlineStart(e.target.value)}
                                style={{ width: 76, height: 28, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 6, color: 'var(--text-color)', fontSize: 11, padding: '0 5px' }} />
                              <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>→</span>
                              <input type="time" value={inlineEnd} onChange={e => setInlineEnd(e.target.value)}
                                style={{ width: 76, height: 28, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 6, color: 'var(--text-color)', fontSize: 11, padding: '0 5px' }} />
                            </div>
                            <div style={{ display: 'flex', gap: 5 }}>
                              <select value={inlineCatId} onChange={e => setInlineCatId(e.target.value ? Number(e.target.value) : '')}
                                style={{ flex: 1, height: 28, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 6, color: 'var(--text-color)', fontSize: 11, padding: '0 5px' }}>
                                <option value="">Category...</option>
                                {allCategory.filter(c => !allCategory.some(x => x.parentId === c.id)).sort((a,b) => a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{catPath(c.id)}</option>)}
                              </select>
                              {inlineTaskOpts.length > 0 && (
                                <select value={inlineTaskId} onChange={e => setInlineTaskId(e.target.value ? Number(e.target.value) : '')}
                                  style={{ flex: 1, height: 28, background: 'var(--bg-input, var(--btn-user-form))', border: '1px solid var(--border-input)', borderRadius: 6, color: 'var(--text-color)', fontSize: 11, padding: '0 5px' }}>
                                  <option value="">Task...</option>
                                  {inlineTaskOpts.sort((a,b)=>a.label.localeCompare(b.label)).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button type="button" onClick={saveInlineEdit} disabled={savingInline}
                                style={{ height: 26, padding: '0 12px', borderRadius: 7, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#2563eb)', color: 'white', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                                {savingInline && <CircularProgress size={9} color="inherit" />} Save
                              </button>
                              <button type="button" onClick={() => setInlineEditId(null)}
                                style={{ height: 26, padding: '0 10px', borderRadius: 7, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                          </div>

                        ) : (
                          /* ── Normal view row ── */
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', transition: 'background .1s', background: selectedEntryIds.has(entry.id) ? 'rgba(37,99,235,.06)' : hoveredEntry === entry.id ? 'var(--none-button, rgba(0,0,0,.03))' : 'transparent' }}
                            onMouseEnter={() => setHoveredEntry(entry.id)}
                            onMouseLeave={() => setHoveredEntry(null)}
                          >
                            <input
                              type="checkbox"
                              checked={selectedEntryIds.has(entry.id)}
                              onChange={e => {
                                setSelectedEntryIds(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(entry.id); else next.delete(entry.id);
                                  return next;
                                });
                              }}
                              style={{ cursor: 'pointer', flexShrink: 0 }}
                              onClick={ev => ev.stopPropagation()}
                            />
                            <div style={{ width: 26, fontSize: 10, fontWeight: 800, color: 'var(--text-color-calendar-week)', textTransform: 'uppercase' as const, flexShrink: 0 }}>
                              {DAY[Number(entry.day_number)]}
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2, #5a6880)', fontVariantNumeric: 'tabular-nums' as const, flexShrink: 0, whiteSpace: 'nowrap' as const }}>
                              {entry.startTime}–{entry.endTime}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, marginLeft: 3 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                                {catPath(entry.categoryId)}
                              </div>
                              {entry.taskId && (
                                <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                                  {resolveTaskNames(entry.taskId)}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0, opacity: hoveredEntry === entry.id ? 1 : 0, transition: 'opacity .15s' }}>
                              <button type="button" style={abtn()} onClick={() => openInlineEdit(entry)} title={t('Edit') || 'Edit'}><SvgEdit /></button>
                              <button type="button" style={abtn('del')} onClick={() => { setDeletingEntry(entry.id); setDeletingGroup(null); setInlineEditId(null); }} title={t('Delete') || 'Delete'}><SvgTrash /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SidePanel>

      {/* Inner drawer for editing a template entry */}
      {editingEntryId !== null && (
        <SidePanel open={editingEntryId !== null} onClose={() => setEditingEntryId(null)}>
          <EditTemplate
            setOpenEditTemp={() => setEditingEntryId(null)}
            teamIdTemp={editingEntryId}
            openCheckModal={openCheckModal}
            is24HourFormat={is24HourFormat}
            refresh={() => { refresh(); setEditingEntryId(null); }}
            taskOrTasks={taskOrTasks}
          />
        </SidePanel>
      )}
    </>
  );
};

export default TemplatesPanel;
